import React, { useState, useEffect } from 'react';
import { FiFileText, FiDownload, FiPlus, FiCheck, FiX, FiSquare } from 'react-icons/fi';
import { getPrintLayouts, getOutputImages, getImageUrl } from '../services/api';
import './PrintLayout.css';

/**
 * PrintLayout Component
 * Create print-ready PDFs with multiple passport photos
 */
// Default layouts (fallback if API fails)
const DEFAULT_LAYOUTS = [
  { name: '1x2', max_photos: 2, description: '2 photos in a row' },
  { name: '2x2', max_photos: 4, description: '4 photos (2x2 grid)' },
  { name: '2x3', max_photos: 6, description: '6 photos (2x3 grid)' },
  { name: '3x2', max_photos: 6, description: '6 photos (3x2 grid)' },
  { name: '3x3', max_photos: 9, description: '9 photos (3x3 grid)' },
  { name: '4x4', max_photos: 16, description: '16 photos (4x4 grid)' },
];

const PrintLayout = () => {
  const [layouts, setLayouts] = useState(DEFAULT_LAYOUTS);
  const [outputImages, setOutputImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState('2x2');
  const [pageSize, setPageSize] = useState('A4');
  const [spacing, setSpacing] = useState(10);
  const [loading, setLoading] = useState(false);
  const [loadingImages, setLoadingImages] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch layouts and images on mount with cleanup
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        setLoadingImages(true);
        const [layoutsRes, imagesRes] = await Promise.all([
          getPrintLayouts().catch(() => ({ layouts: DEFAULT_LAYOUTS })),
          getOutputImages().catch(() => ({ images: [] }))
        ]);
        
        if (isMounted) {
          setLayouts(layoutsRes?.layouts?.length > 0 ? layoutsRes.layouts : DEFAULT_LAYOUTS);
          setOutputImages(imagesRes?.images || []);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error loading data:', err);
          // Don't show error, use defaults
        }
      } finally {
        if (isMounted) {
          setLoadingImages(false);
        }
      }
    };
    
    fetchData();
    
    // Cleanup
    return () => {
      isMounted = false;
    };
  }, []);

  // Refresh images list
  const refreshImages = async () => {
    try {
      const imagesRes = await getOutputImages();
      setOutputImages(imagesRes.images || []);
    } catch (err) {
      console.error('Error refreshing images:', err);
    }
  };

  // Toggle image selection
  const toggleImageSelection = (filename) => {
    setSelectedImages(prev => {
      if (prev.includes(filename)) {
        return prev.filter(f => f !== filename);
      }
      // Check max photos for selected layout
      const layout = layouts.find(l => l.name === selectedLayout);
      if (layout && prev.length >= layout.max_photos) {
        setError(`Maximum ${layout.max_photos} photos allowed for ${selectedLayout} layout`);
        setTimeout(() => setError(null), 3000);
        return prev;
      }
      return [...prev, filename];
    });
  };

  // Get total photos needed for layout
  const getTotalPhotos = () => {
    const [cols, rows] = selectedLayout.split('x').map(Number);
    return cols * rows;
  };

  // Get repeated images to fill the grid
  const getRepeatedImages = () => {
    if (selectedImages.length === 0) return [];
    const totalNeeded = getTotalPhotos();
    const repeated = [];
    for (let i = 0; i < totalNeeded; i++) {
      repeated.push(selectedImages[i % selectedImages.length]);
    }
    return repeated;
  };

  // Get image URL by ID
  const getImageById = (imageId) => {
    const img = outputImages.find(i => (typeof i === 'object' ? i.id : i) === imageId);
    return typeof img === 'object' ? img.dataUrl : getImageUrl(`/outputs/${img}`);
  };

  // Create print PDF or open print dialog
  const handleCreatePrint = async () => {
    if (selectedImages.length === 0) {
      setError('Please select at least one image');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const [cols, rows] = selectedLayout.split('x').map(Number);
      const repeatedImages = getRepeatedImages();
      const imageUrls = repeatedImages.map(id => getImageById(id));

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Print Layout - ${selectedLayout}</title>
            <style>
              @page { size: ${pageSize}; margin: 5mm; }
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                width: 100%; 
                height: 100vh;
                display: flex;
                justify-content: center;
                align-items: center;
                background: white;
              }
              .grid { 
                display: grid; 
                grid-template-columns: repeat(${cols}, 1fr); 
                grid-template-rows: repeat(${rows}, 1fr);
                gap: ${spacing}px; 
                padding: ${spacing}px;
                width: 100%;
                max-width: ${pageSize === 'A4' ? '210mm' : '8.5in'};
                aspect-ratio: ${pageSize === 'A4' ? '210/297' : '8.5/11'};
                background: white;
              }
              .photo { 
                width: 100%;
                height: 100%;
                object-fit: cover; 
                border: 1px solid #eee;
                border-radius: 2px;
              }
            </style>
          </head>
          <body>
            <div class="grid">
              ${imageUrls.map(src => `<img class="photo" src="${src}" />`).join('')}
            </div>
            <script>
              window.onload = () => { 
                setTimeout(() => window.print(), 500); 
              };
            </script>
          </body>
          </html>
        `);
        printWindow.document.close();
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.message || 'Failed to create print layout');
    } finally {
      setLoading(false);
    }
  };

  // Get current layout info
  const currentLayout = layouts.find(l => l.name === selectedLayout) || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-2 text-gray-900 dark:text-white flex items-center gap-3">
          <FiFileText className="text-blue-600" /> Print Layout
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Create print-ready PDFs with multiple passport photos on a single sheet
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Settings */}
        <div className="space-y-6">
          {/* Layout Selection */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FiSquare className="text-blue-600" /> Layout
            </h2>
            
            <div className="grid grid-cols-2 gap-2">
              {layouts.map((layout) => (
                <button
                  key={layout.name}
                  onClick={() => setSelectedLayout(layout.name)}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${
                    selectedLayout === layout.name
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{layout.name}</p>
                  <p className="text-xs text-gray-500">{layout.max_photos} photos</p>
                </button>
              ))}
            </div>

            {currentLayout.description && (
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                {currentLayout.description}
              </p>
            )}
          </div>

          {/* Page Settings */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Page Settings</h2>
            
            {/* Page Size */}
            <div className="mb-4">
              <label className="text-sm font-semibold text-gray-500 mb-2 block">Page Size</label>
              <div className="grid grid-cols-2 gap-2">
                {['A4', 'Letter'].map((size) => (
                  <button
                    key={size}
                    onClick={() => setPageSize(size)}
                    className={`py-2 px-4 rounded-lg border-2 transition-all text-sm font-bold ${
                      pageSize === size
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Spacing */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-500">Spacing</label>
                <span className="text-sm font-bold text-blue-600">{spacing}pt</span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                value={spacing}
                onChange={(e) => setSpacing(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          </div>

          {/* Create Button */}
          <button
            onClick={handleCreatePrint}
            disabled={loading || selectedImages.length === 0}
            className={`w-full py-4 px-6 rounded-xl font-bold text-white transition-all shadow-md flex items-center justify-center gap-2 ${
              loading || selectedImages.length === 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/20'
            }`}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Creating PDF...
              </>
            ) : (
              <>
                <FiDownload /> Create Print PDF
              </>
            )}
          </button>

          {/* Status Messages */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
              <FiX /> {error}
            </div>
          )}

          {success && (
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 text-sm flex items-center gap-2">
              <FiCheck /> PDF downloaded successfully!
            </div>
          )}

          {/* Print Preview */}
          {selectedImages.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Print Preview</h2>
              <div 
                className="bg-white border border-gray-300 rounded-lg overflow-hidden mx-auto"
                style={{ 
                  aspectRatio: pageSize === 'A4' ? '210/297' : '8.5/11',
                  maxHeight: '300px'
                }}
              >
                <div 
                  className="w-full h-full grid p-1"
                  style={{ 
                    gridTemplateColumns: `repeat(${selectedLayout.split('x')[0]}, 1fr)`,
                    gridTemplateRows: `repeat(${selectedLayout.split('x')[1]}, 1fr)`,
                    gap: `${Math.max(1, spacing / 5)}px`
                  }}
                >
                  {getRepeatedImages().map((imageId, index) => (
                    <div key={index} className="bg-gray-100 rounded overflow-hidden">
                      <img 
                        src={getImageById(imageId)} 
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                {getTotalPhotos()} photos on {pageSize} page
              </p>
            </div>
          )}
        </div>

        {/* Right Column - Image Selection */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Select Photos ({selectedImages.length}/{currentLayout.max_photos || 0})
              </h2>
              <button
                onClick={refreshImages}
                className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
              >
                Refresh
              </button>
            </div>

            {/* Upload Button */}
            <div className="mb-4">
              <label className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-xl cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all">
                <FiPlus className="text-blue-600" />
                <span className="text-sm font-semibold text-blue-600">Add Photos for Print</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    files.forEach(file => {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        const dataUrl = event.target?.result;
                        if (dataUrl) {
                          const id = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                          setOutputImages(prev => [...prev, { id, dataUrl, name: file.name }]);
                        }
                      };
                      reader.readAsDataURL(file);
                    });
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            {loadingImages ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            ) : outputImages.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FiFileText className="text-3xl text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No Photos Yet</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                  Upload photos using the button above
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {outputImages.map((img) => {
                  // Handle both local images (object with dataUrl) and server images (string filename)
                  const isLocalImage = typeof img === 'object' && img.dataUrl;
                  const imageId = isLocalImage ? img.id : img;
                  const imageUrl = isLocalImage ? img.dataUrl : getImageUrl(`/outputs/${img}`);
                  const imageName = isLocalImage ? img.name : img;
                  const isSelected = selectedImages.includes(imageId);
                  
                  return (
                    <button
                      key={imageId}
                      onClick={() => toggleImageSelection(imageId)}
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                        isSelected
                          ? 'border-blue-600 ring-2 ring-blue-600/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={imageUrl}
                        alt={imageName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23eee" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999">?</text></svg>';
                        }}
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">
                            <FiCheck />
                          </div>
                        </div>
                      )}
                      {/* Delete button for local images */}
                      {isLocalImage && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOutputImages(prev => prev.filter(i => (typeof i === 'object' ? i.id : i) !== imageId));
                            setSelectedImages(prev => prev.filter(id => id !== imageId));
                          }}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-xs"
                        >
                          <FiX size={12} />
                        </button>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Selected Images Preview */}
            {selectedImages.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-500 mb-3">Selected for Print ({selectedImages.length})</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedImages.map((imageId, index) => {
                    const img = outputImages.find(i => (typeof i === 'object' ? i.id : i) === imageId);
                    const displayName = typeof img === 'object' ? img.name : imageId;
                    return (
                      <div
                        key={imageId}
                        className="flex items-center gap-2 bg-gray-100 dark:bg-slate-700 rounded-lg px-3 py-1.5"
                      >
                        <span className="text-xs font-semibold text-blue-600">#{index + 1}</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[100px]">
                          {displayName?.substring(0, 12) || 'Image'}...
                        </span>
                        <button
                          onClick={() => toggleImageSelection(imageId)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintLayout;
